import { Coords } from "@hokuto/jam-core";
import { JamHandlerType, ElementObserverProps } from "../../types.js";

export type ElementObserverActionType<T extends Element> = keyof Pick<
  ElementObserverProps<T>,
  "onChange" | "onClick" | "onMove" | "onLeave"
>;

export type ElementObserverAction<
  T extends Element,
  K extends ElementObserverActionType<T>
> = Required<ElementObserverProps<T>>[K];

export type ElementCallbackHandler<
  T extends Element,
  K extends ElementObserverActionType<T>
> = JamHandlerType<Parameters<ElementObserverAction<T, K>>, void>;

export class ElementHandlers {
  static addClickListener<T extends Element>(
    element: T,
    callback: ElementCallbackHandler<T, "onClick">
  ) {
    let clickAction: ReturnType<typeof setTimeout> | undefined;
    function listener(event: MouseEvent) {
      const { width, height } = ElementHandlers.getElementSize(element);
      const { point, outer } = ElementHandlers.getEventPosition({
        element,
        event,
      });
      if (outer) return;
      if (clickAction) {
        clearTimeout(clickAction);
        clickAction = undefined;
      } else {
        clickAction = setTimeout(() => {
          callback({ width, height, point, event });
          clickAction = undefined;
        }, 500);
      }
    }
    window.addEventListener("click", listener);
    return listener;
  }

  static removeClickListener(listener: (event: MouseEvent) => void) {
    window.removeEventListener("click", listener);
  }

  static addMouseMoveListener<T extends Element>(
    element: T,
    moveCallback: ElementCallbackHandler<T, "onMove">,
    leaveCallback: ElementCallbackHandler<T, "onLeave">
  ) {
    let outer = true;
    function listener(event: MouseEvent) {
      if (!element) return;
      const { width, height } = ElementHandlers.getElementSize(element);
      const { point, outer: currentOuter } = ElementHandlers.getEventPosition({
        element,
        event,
      });
      !currentOuter && moveCallback({ width, height, point, event });
      currentOuter &&
        currentOuter !== outer &&
        leaveCallback({ width, height, point, event });

      outer = currentOuter;
    }
    window.addEventListener("mousemove", listener);
    return listener;
  }

  static removeMouseMoveListener(listener: (event: MouseEvent) => void) {
    window.removeEventListener("mousemove", listener);
  }

  static addResizeObserver<T extends Element>(
    element: T,
    callback: ElementCallbackHandler<T, "onChange">
  ) {
    const previousSize = ElementHandlers.getElementSize(element);

    let { width: previousWidth, height: previousHeight } = previousSize;

    callback({ width: previousWidth, height: previousHeight });

    const resizeObserver = new ResizeObserver(([{ contentRect }]) => {
      let { width, height } = contentRect;
      width = Math.floor(width);
      height = Math.floor(height);

      if (width === previousWidth && height === previousHeight) return;
      previousWidth = width;
      previousHeight = height;
      callback({ width, height });
    });
    resizeObserver.observe(element);
    return resizeObserver;
  }

  static removeResizeObserver<T extends Element>(
    element: T,
    resizeObserver: ResizeObserver
  ) {
    resizeObserver.unobserve(element);
  }

  static getElementSize<T extends Element>(element: T) {
    const { width, height } = element.getBoundingClientRect();
    return { width: Math.floor(width), height: Math.floor(height) };
  }

  static getEventPosition<T extends Element>(args: {
    element: T;
    event: MouseEvent;
  }): { point: Coords; outer: boolean } {
    const { element, event } = args;
    const rect = element.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const outer = x < 0 || x > rect.width || y < 0 || y > rect.height;
    return { point: [x, y], outer };
  }

  static targetIncluded(
    container: Element,
    target?: Element | EventTarget | null
  ): boolean {
    if (!target) return false;
    const element = target as Element;
    if (target === container) return true;
    return ElementHandlers.targetIncluded(container, element.parentElement);
  }
}
